/**
 * Фоновая подгрузка уроков — так, чтобы «Начать урок» и «Следующий урок»
 * открывались мгновенно, но без лишних обращений к модели.
 *
 * Три уровня «умности»:
 * 1. Текущий урок с главной/карты просим только после паузы на экране
 *    (`LESSON_PREFETCH_DELAY_MS`): случайно заглянувший и закрывший приложение
 *    ученик токенов не тратит.
 * 2. Следующий урок просим у сервера только когда ученик реально начал
 *    заниматься (ответил на первое задание или дочитал историю) — генерация
 *    успевает пройти, пока идёт урок.
 * 3. Скрытая вкладка ставит всё на паузу, а запросы в полёте отменяет.
 *
 * Ошибки фоновых запросов не показываются: не вышло — экран урока загрузит
 * урок сам, как обычно.
 */

import type { CurriculumLessonView } from '@aptile/shared';

import { ApiClientError } from '../api/client';
import { fetchCurriculumLesson, prefetchNextLesson as requestNextLesson } from '../api/curriculum';
import {
  isPrefetchedLessonFresh,
  lessonPrefetchKey,
  useLessonPrefetchStore,
} from '../store/lessonPrefetchStore';
import { createPrefetchScheduler } from './prefetchScheduler';

/** Пауза «оседлости» на экране до старта подгрузки. */
export const LESSON_PREFETCH_DELAY_MS = 2_500;

/** Сервер готовит урок — спрашиваем реже, чем экран урока: это фон. */
const POLL_MS = 3_000;
/** Дольше фоново не ждём — синхронная загрузка экрана справится сама. */
const MAX_WAIT_MS = 4 * 60 * 1000;

function documentVisible(): boolean {
  return typeof document === 'undefined' || document.visibilityState === 'visible';
}

const scheduler = createPrefetchScheduler({
  debounceMs: LESSON_PREFETCH_DELAY_MS,
  setTimeout: (callback, ms) => globalThis.setTimeout(callback, ms),
  clearTimeout: (handle) => globalThis.clearTimeout(handle as ReturnType<typeof setTimeout>),
  isVisible: documentVisible,
});

interface InFlight {
  readonly controller: AbortController;
  readonly promise: Promise<CurriculumLessonView | null>;
}

/** Запросы урока в полёте — по ключу: дубль не стартует второй. */
const inFlight = new Map<string, InFlight>();
/** Какие «следующие» уроки уже просили подготовить в этой сессии. */
const nextRequested = new Set<string>();

/** Ждать видимости вкладки: скрыли — отменяем всё в полёте. */
let observingVisibility = false;
function observeVisibility(): void {
  if (typeof document === 'undefined' || observingVisibility) return;
  observingVisibility = true;
  document.addEventListener('visibilitychange', () => {
    const visible = document.visibilityState === 'visible';
    scheduler.setVisible(visible);
    if (visible) return;
    for (const entry of inFlight.values()) entry.controller.abort();
    inFlight.clear();
  });
}

const sleep = (ms: number, signal: AbortSignal): Promise<void> =>
  new Promise((resolve) => {
    if (signal.aborted) {
      resolve();
      return;
    }
    const timer = globalThis.setTimeout(finish, ms);
    function finish(): void {
      globalThis.clearTimeout(timer);
      signal.removeEventListener('abort', finish);
      resolve();
    }
    signal.addEventListener('abort', finish, { once: true });
  });

/**
 * Запросить урок, пережидая «урок готовится» (`LESSON_PREPARING`), пока
 * фоновое ожидание не вышло. Отмена сигнала и любая другая ошибка — `null`.
 */
async function fetchWithWaiting(
  userId: string,
  skillId: string,
  signal: AbortSignal,
): Promise<CurriculumLessonView | null> {
  const startedAt = Date.now();
  for (;;) {
    if (signal.aborted) return null;
    try {
      return await fetchCurriculumLesson(userId, skillId, signal);
    } catch (error) {
      const preparing = error instanceof ApiClientError && error.code === 'LESSON_PREPARING';
      if (!preparing || signal.aborted || Date.now() - startedAt >= MAX_WAIT_MS) return null;
      await sleep(POLL_MS, signal);
    }
  }
}

/**
 * Начать (или присоединиться к уже идущей) подгрузке урока. Результат
 * кладётся в кэш; ошибки проглатываются. `null` — не вышло или отменили.
 */
function startFetch(userId: string, skillId: string): Promise<CurriculumLessonView | null> {
  observeVisibility();
  const key = lessonPrefetchKey(userId, skillId);
  const existing = inFlight.get(key);
  if (existing !== undefined) return existing.promise;

  const controller = new AbortController();
  const promise = fetchWithWaiting(userId, skillId, controller.signal)
    .then((view) => {
      if (view !== null && !controller.signal.aborted) {
        useLessonPrefetchStore.getState().put(key, view);
      }
      return view;
    })
    .catch(() => null)
    .finally(() => {
      if (inFlight.get(key)?.controller === controller) inFlight.delete(key);
    });

  inFlight.set(key, { controller, promise });
  return promise;
}

/**
 * Запланировать подгрузку текущего урока с задержкой «оседлости».
 * Повторный вызов для того же урока — no-op.
 */
export function scheduleLessonPrefetch(userId: string, skillId: string): void {
  observeVisibility();
  const key = lessonPrefetchKey(userId, skillId);
  if (useLessonPrefetchStore.getState().has(key) || inFlight.has(key)) return;
  scheduler.schedule(key, () => {
    void startFetch(userId, skillId);
  });
}

/** Экран ушёл: отменить ещё не начатую подгрузку (запрос в полёте живёт — его заберёт экран урока). */
export function cancelScheduledLessonPrefetch(userId: string, skillId: string): void {
  scheduler.cancel(lessonPrefetchKey(userId, skillId));
}

/** Готовый урок из памяти или `null`. Устаревший (старше 10 минут) не отдаётся. */
export function takePrefetchedLesson(userId: string, skillId: string): CurriculumLessonView | null {
  return useLessonPrefetchStore.getState().take(lessonPrefetchKey(userId, skillId));
}

/**
 * Подгрузка уже в пути — дождаться её, а не дублировать запрос.
 * `null` — ничего не идёт, экран урока загружает сам.
 */
export function claimPrefetchedLesson(userId: string, skillId: string): Promise<CurriculumLessonView | null> | null {
  const entry = inFlight.get(lessonPrefetchKey(userId, skillId));
  if (entry === undefined || entry.controller.signal.aborted) return null;
  return entry.promise;
}

/** Урок уже лежит в памяти и ещё свеж — для зелёной точки на кнопке. */
export function isLessonPrefetched(userId: string, skillId: string): boolean {
  return isPrefetchedLessonFresh(useLessonPrefetchStore.getState().lessons[lessonPrefetchKey(userId, skillId)]);
}

/**
 * Ученик проявил вовлечённость (ответил на задание или дочитал историю):
 * пусть сервер готовит следующий урок, пока идёт текущий. Содержимое не
 * запрашивается — следующая ступень ещё закрыта. Повторно не просим.
 */
export function prefetchNextLessonInBackground(userId: string, skillId: string): void {
  observeVisibility();
  if (!documentVisible()) return;
  const key = `next|${lessonPrefetchKey(userId, skillId)}`;
  if (nextRequested.has(key)) return;
  nextRequested.add(key);
  void requestNextLesson(userId, skillId).catch(() => undefined);
}

/**
 * После итога урока: следующий уже открыт — тянем его в память сразу
 * (без задержки «оседлости»), чтобы кнопка «Дальше» открылась без лоадера.
 */
export function warmLesson(userId: string, skillId: string): void {
  if (!documentVisible()) return;
  const key = lessonPrefetchKey(userId, skillId);
  if (useLessonPrefetchStore.getState().has(key) || inFlight.has(key)) return;
  void startFetch(userId, skillId);
}
